import { useState, useCallback } from "react";

export type JoyrideStep = {
  id: string;
  targetId: string;
  title: string;
  description: string;
  badgeText?: string;
  preferredPlacement?: "top" | "bottom" | "left" | "right" | "center";
};

export const EXPLORER_JOYRIDE_STEPS: readonly JoyrideStep[] = [
  {
    id: "header-nav",
    targetId: "tour-header-nav",
    title: "Welcome to MetricMap UK 🇬🇧",
    badgeText: "Step 1 of 8",
    description:
      "Explore official ONS data across UK Local Authorities, Counties, and Regions. Use the top bar to switch routes, toggle light/dark theme, or copy direct share links.",
    preferredPlacement: "bottom",
  },
  {
    id: "overview-card",
    targetId: "tour-overview-card",
    title: "Metric Overview & National Stats",
    badgeText: "Step 2 of 8",
    description:
      "Inspect the currently selected dataset, live ONS sample sizes, units of measure, and UK national mean benchmarks. On laptop displays, hover map regions to view area statistics.",
    preferredPlacement: "right",
  },
  {
    id: "map-controls",
    targetId: "tour-map-controls",
    title: "Dataset Selector & Boundary Controls",
    badgeText: "Step 3 of 8",
    description:
      "Switch between 17 official ONS datasets including Pay, Savings, Rent Costs, Crime Rates, and EV Density. Toggle boundary resolution between Local Authorities, Counties, and Regions.",
    preferredPlacement: "left",
  },
  {
    id: "composite-tab",
    targetId: "tour-composite-tab",
    title: "Quality-of-Life Index Mode",
    badgeText: "Step 4 of 8",
    description:
      "Switch to the Quality-of-Life Index tab to combine multiple economic, housing, and environmental indicators into a single live 0–100 score.",
    preferredPlacement: "left",
  },
  {
    id: "composite-pane",
    targetId: "tour-composite-pane",
    title: "Strategy Presets & Weight Sliders",
    badgeText: "Step 5 of 8",
    description:
      "Choose 1-click strategy presets (like Balanced QoL, Economic Prosperity, or Green & Clean) or adjust individual metric sliders from 0% to 100%.",
    preferredPlacement: "left",
  },
  {
    id: "region-comparison",
    targetId: "tour-comparison-flyout",
    title: "Side-by-Side Region Comparison",
    badgeText: "Step 6 of 8",
    description:
      "Selecting any 2 UK districts triggers this live comparison flyout, comparing local values, absolute differences, percentage variances, and national benchmark deltas.",
    preferredPlacement: "left",
  },
  {
    id: "ranking-strip",
    targetId: "tour-ranking-strip",
    title: "Regional Ranking & Pinned Areas",
    badgeText: "Step 7 of 8",
    description:
      "View top and bottom ranked areas with per-district historical trend sparklines. Click the pin icon on any district card to build a custom shortcut list.",
    preferredPlacement: "top",
  },
  {
    id: "export-nav",
    targetId: "tour-export-nav",
    title: "Full Leaderboard & Data Export",
    badgeText: "Step 8 of 8",
    description:
      "Navigate to the /leaderboard route to sort, filter, and search full data grids, or export high-resolution RFC 4180 CSV files and GeoJSON boundaries.",
    preferredPlacement: "bottom",
  },
];

const STORAGE_KEY = "metricmap_joyride_completed";

export function useExplorerJoyride() {
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const handleStartTour = useCallback(() => {
    setStepIndex(0);
    setIsRunning(true);
  }, []);

  const handleStopTour = useCallback(() => {
    setIsRunning(false);
    if (globalThis.localStorage !== undefined) {
      try {
        globalThis.localStorage.setItem(STORAGE_KEY, "true");
      } catch {
        // Ignore storage errors in restricted contexts
      }
    }
  }, []);

  const handleNextStep = useCallback(() => {
    setStepIndex((current) => {
      if (current >= EXPLORER_JOYRIDE_STEPS.length - 1) {
        handleStopTour();
        return current;
      }
      return current + 1;
    });
  }, [handleStopTour]);

  const handlePrevStep = useCallback(() => {
    setStepIndex((current) => (current > 0 ? current - 1 : current));
  }, []);

  const handleWelcomeDismissed = useCallback(() => {
    let completed = false;
    if (globalThis.localStorage !== undefined) {
      try {
        completed = globalThis.localStorage.getItem(STORAGE_KEY) === "true";
      } catch {
        completed = false;
      }
    }
    if (!completed) {
      handleStartTour();
    }
  }, [handleStartTour]);

  const currentStep = EXPLORER_JOYRIDE_STEPS[stepIndex] || EXPLORER_JOYRIDE_STEPS[0];

  return {
    isRunning,
    stepIndex,
    totalSteps: EXPLORER_JOYRIDE_STEPS.length,
    currentStep,
    steps: EXPLORER_JOYRIDE_STEPS,
    handleStartTour,
    handleStopTour,
    handleNextStep,
    handlePrevStep,
    handleWelcomeDismissed,
  };
}
